import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  Download,
  Fingerprint,
  ShieldCheck,
  Scale,
  FileCheck,
} from 'lucide-react';

// ─── The Seven Conditions ─────────────────────────────────────────────────
const conditions = [
  { n: 1, label: 'Software can decide and act', base: 'holds', kojiru: 'holds', ours: false },
  { n: 2, label: 'It can pay', base: 'holds', kojiru: 'holds', ours: false },
  { n: 3, label: 'You can prove which agent it is', base: 'no', kojiru: 'partly', ours: true, kojiruNote: 'cryptographically' },
  { n: 4, label: 'Someone is answerable in law', base: 'holds', kojiru: 'holds', ours: false },
  { n: 5, label: 'It can be authorized within limits', base: 'partly', kojiru: 'holds', ours: true, kojiruNote: 'atomically' },
  { n: 6, label: 'There is recourse when it goes wrong', base: 'no', kojiru: 'holds', ours: true, kojiruNote: 'public evidence record' },
  { n: 7, label: 'A supervisor is content', base: 'no', kojiru: 'partial', ours: true, kojiruNote: 'evidence produced' },
];

const StatusBadge = ({ status }) => {
  const map = {
    holds: { icon: CheckCircle2, cls: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10', label: 'HOLDS' },
    no: { icon: XCircle, cls: 'text-rose-400 border-rose-500/40 bg-rose-500/10', label: 'NO' },
    partly: { icon: AlertTriangle, cls: 'text-amber-400 border-amber-500/40 bg-amber-500/10', label: 'PARTLY' },
    partial: { icon: AlertTriangle, cls: 'text-amber-400 border-amber-500/40 bg-amber-500/10', label: 'PARTIAL' },
  };
  const { icon: Icon, cls, label } = map[status] || map.no;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-bold tracking-[1.2px] ${cls}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

// ─── Sections that walk each open condition ───────────────────────────────
const sections = [
  {
    n: 3,
    icon: Fingerprint,
    title: 'You can prove which agent it is',
    gap: 'MCP\'s own identity fields are not verified by the protocol. A2A signs the published card, not the agent behind a given transaction. ERC-8004 is still a draft a year after publication.',
    fit: 'The KYPA Directory issues a cryptographically-bound identifier, the KYPA ID, to each registered agent, derived from a Base wallet the operator controls. A transaction against a KYPA agent can be verified in one step: hash the counterparty wallet, look it up in the Directory, read the pledge amount and outcome history.',
    honestLimits: [
      'KYPA does not verify the human principal behind the operator. That remains the PSP\'s or bank\'s job.',
      'The identity is only as strong as the operator\'s key hygiene. A leaked key is a leaked identity.',
    ],
    verdict: 'Partly: a public, machine-checkable, on-chain identity claim.',
    proof: { label: 'View KYPA Directory', href: '/kypa/directory', internal: true },
  },
  {
    n: 5,
    icon: Scale,
    title: 'It can be authorized within limits',
    gap: 'The word "revoke" appears nowhere in x402, nowhere in a global cloud hyperscaler\'s AP2, nowhere in a major card network Agent Pay. A major card network alone publishes a cancel path, and does not say how quickly canceling takes effect.',
    fit: 'Revocation on Kojiru is not a message an operator sends. It is a state a smart contract enforces. Every KYPA agent pledges USDC as slashable collateral at a defined tier. Break the promise, and the breach is recorded publicly next to the pledge, a public commitment signal anyone can check.',
    honestLimits: [
      'This only holds for counterparties that have opted into KYPA. It does not fix revocation for the broader agent-payments market.',
    ],
    verdict: 'Holds, publicly, without a discretionary review window.',
    proof: { label: 'KojiruACSHook on BaseScan', href: 'https://basescan.org/address/0xaaae9ae4d52fe13384522e0ef4114b0a5f5d62d68', internal: false },
  },
  {
    n: 6,
    icon: ShieldCheck,
    title: 'There is recourse when it goes wrong',
    gap: 'On-chain settlement is built not to reverse. Neither major stablecoin issuer publishes a route by which a counterparty dispute triggers an intervention. A major card network\'s rules now make cardholders responsible for anything their agent does.',
    fit: 'The KojiruACSHook contract implements narrow, on-chain recourse. When an attestation and a proof-of-breach coincide, the breach is recorded on-chain next to the operator&apos;s pledge. No custody. Not insurance. No promise of repayment.',
    honestLimits: [
      'Recourse triggers only for promises whose breach is objectively provable on-chain.',
      'Subjective quality disputes, off-chain evidence, and cross-jurisdiction issues remain open: those are for humans and lawyers.',
    ],
    verdict: 'Holds as evidence, for KYPA-member transactions. Remedies stay with the parties.',
    proof: { label: 'Read the x402-KYPA brief', href: '/downloads/kojiru-x402-kypa-brief.pdf', internal: true },
  },
  {
    n: 7,
    icon: FileCheck,
    title: 'A supervisor is content',
    gap: 'The Bank of England: risks are "likely to increase, potentially rapidly." No accountancy body has yet published guidance on how approval limits should treat a payment initiated by software.',
    fit: 'We cannot make a supervisor content: that is not our decision. What we can do is publish an artifact a supervisor can point at. The KYPA Directory produces, for every registered agent, a permanent on-chain record of pledges, breach records, tier changes, and enrollments: the evidence class an auditor or internal risk committee needs before they can approve anything.',
    honestLimits: [
      'This is not a substitute for supervisory approval.',
      'We do not pretend we can move a regulator. We can hand a compliance officer what they need to move themselves.',
    ],
    verdict: 'Partial: evidence produced, decision left to the supervisor.',
    proof: { label: 'Six-layer KYA mapping', href: '/kya', internal: true },
  },
];

export default function SevenConditionsResponse() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Three More Conditions to Go · Kojiru</title>
        <meta
          name="description"
          content="A response to a digital-money podcast · The Agentic Economy Episode 1. Kojiru maps to conditions 3, 5, 6, and 7."
        />
        <meta property="og:title" content="Three More Conditions to Go · Kojiru" />
        <meta
          property="og:description"
          content="Seven things have to be true before software can spend your money. Three are. Here is where Kojiru advances the other four."
        />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs font-bold tracking-[2px] text-rose-400 mb-6">
              RESPONSE · PUBLIC · AUGUST 22, 2026
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Three More Conditions to Go
            </h1>
            <p className="text-lg text-slate-300 italic mb-2">
              A response to <span className="text-white">A digital-money podcast · The Agentic Economy · Episode 1</span>
            </p>
            <p className="text-sm text-slate-500">Wayne Faulkner · Founder, Kojiru, LLC</p>
          </motion.div>
        </div>
      </section>

      {/* Framework recap */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">What DH K. wrote, in one sentence</h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Seven things have to be true before software can spend your money. Three of them are. The four
            missing conditions are not hard engineering problems. They are questions about who answers for
            what.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            We agree, and we have spent the last year building for the four missing conditions specifically.
          </p>
          <p className="text-slate-400 leading-relaxed">
            This is a response, not a pitch. Read Episode 1 first. Everything below assumes his framework and
            his scoring, and takes each of the four unresolved conditions in turn. Where we can advance a
            condition, we say how, with a link to the on-chain evidence. Where we cannot, we say so.
          </p>
        </div>
      </section>

      {/* The score table */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">
            THE SCORE, INSIDE A KYPA PERIMETER
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mb-8 leading-tight">
            Base market gets to 3 of 7. Kojiru inside its perimeter gets to 6 of 7.
          </h2>

          <div className="border border-white/10 rounded-xl overflow-hidden bg-[#0f0f16]">
            <div className="grid grid-cols-[auto,1fr,140px,180px] gap-x-4 px-4 sm:px-6 py-4 border-b border-white/10 bg-white/5 text-[10px] font-bold tracking-[1.5px] text-slate-400">
              <div>#</div>
              <div>CONDITION</div>
              <div className="text-center">BASE MARKET</div>
              <div className="text-center">WITH KOJIRU</div>
            </div>
            {conditions.map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className={`grid grid-cols-[auto,1fr,140px,180px] gap-x-4 px-4 sm:px-6 py-4 items-center border-b border-white/5 last:border-b-0 ${
                  c.ours ? 'bg-rose-500/[0.03]' : ''
                }`}
                data-testid={`condition-row-${c.n}`}
              >
                <div className="text-slate-500 font-mono text-sm">{c.n}</div>
                <div className="text-white font-medium text-sm sm:text-base">{c.label}</div>
                <div className="flex justify-center">
                  <StatusBadge status={c.base} />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <StatusBadge status={c.kojiru} />
                  {c.kojiruNote && (
                    <span className="text-[10px] text-slate-500 italic">{c.kojiruNote}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-sm text-slate-500 mt-4 italic">
            Rows we address are highlighted. We do not touch conditions 1, 2, and 4: others build those
            layers well.
          </p>
        </div>
      </section>

      {/* Section-by-section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-16">
          {sections.map((s, idx) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="border-l-2 border-rose-500/40 pl-6 sm:pl-10"
              data-testid={`condition-detail-${s.n}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <s.icon className="w-5 h-5 text-rose-400" />
                <div className="text-xs font-bold tracking-[2px] text-slate-500">
                  CONDITION {s.n}
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">{s.title}</h3>

              <div className="mb-6">
                <div className="text-[11px] font-bold tracking-[1.5px] text-rose-400 mb-2">
                  WHERE IT STANDS
                </div>
                <p className="text-slate-300 leading-relaxed italic">&ldquo;{s.gap}&rdquo;</p>
              </div>

              <div className="mb-6">
                <div className="text-[11px] font-bold tracking-[1.5px] text-emerald-400 mb-2">
                  WHERE KOJIRU FITS
                </div>
                <p className="text-slate-200 leading-relaxed">{s.fit}</p>
              </div>

              <div className="mb-6">
                <div className="text-[11px] font-bold tracking-[1.5px] text-amber-400 mb-2">
                  HONEST LIMITS
                </div>
                <ul className="space-y-2">
                  {s.honestLimits.map((limit, i) => (
                    <li key={i} className="text-slate-400 text-sm leading-relaxed flex gap-3">
                      <span className="text-amber-500 mt-1">·</span>
                      <span>{limit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t border-white/10">
                <div>
                  <div className="text-[11px] font-bold tracking-[1.5px] text-slate-500 mb-1">
                    VERDICT
                  </div>
                  <p className="text-white font-semibold">{s.verdict}</p>
                </div>
                {s.proof.internal ? (
                  <Link to={s.proof.href}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                      data-testid={`proof-link-condition-${s.n}`}
                    >
                      {s.proof.label}
                      <ArrowRight className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <a href={s.proof.href} target="_blank" rel="noopener noreferrer">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                      data-testid={`proof-link-condition-${s.n}`}
                    >
                      {s.proof.label}
                      <ExternalLink className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* What we are not */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-black mb-6">Three quick notes on what Kojiru is not</h2>
          <div className="space-y-4">
            <div className="border-l-2 border-white/10 pl-6">
              <p className="text-slate-300">
                <span className="text-white font-bold">Kojiru is not a rail.</span> We do not move payments.
                The USDC issuer, Base, x402, a card-issuing startup, a major card network, and a major card network do. We sit above the rail and record the promise.
              </p>
            </div>
            <div className="border-l-2 border-white/10 pl-6">
              <p className="text-slate-300">
                <span className="text-white font-bold">Kojiru is not a bureau.</span> A major credit bureau, Equifax, and
                TransUnion score humans against SSNs. We score agents against pledged collateral. The two
                systems do not compete; they interoperate.
              </p>
            </div>
            <div className="border-l-2 border-white/10 pl-6">
              <p className="text-slate-300">
                <span className="text-white font-bold">Kojiru is not an arbitrator.</span> The contract reads
                a proof and executes. There is no room for interpretation, and no room for us to hold either
                party&rsquo;s funds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Update block · Three Voices */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">UPDATE &middot; 25 AUGUST 2026</div>
          <h2 className="text-2xl sm:text-3xl font-black mb-6 leading-tight">
            Three analysts, three vantages, ten days. Same gap.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-6">
            Since this response was published, two more analysts have independently arrived at the same
            unresolved condition. Not by coordination. By following the same question to the same place.
          </p>
          <div className="space-y-4 mb-8">
            <div className="border-l-2 border-rose-500/40 pl-5 py-2">
              <div className="text-[10px] font-bold tracking-[1.5px] text-rose-400 mb-1">AUGUST 18 &middot; A STABLECOIN COMPLIANCE ANALYST</div>
              <p className="text-slate-300 leading-relaxed">
                <em className="text-white">A digital-money podcast</em>, Episode 1. The framework this whole response is built on. Seven conditions, three of them hold.
                &ldquo;A control you cannot demonstrate you can stop is not a control.&rdquo;
              </p>
            </div>
            <div className="border-l-2 border-cyan-500/40 pl-5 py-2">
              <div className="text-[10px] font-bold tracking-[1.5px] text-cyan-400 mb-1">AUGUST 21 &middot; A PAYMENTS RESEARCH WRITER</div>
              <p className="text-slate-300 leading-relaxed">
                <em className="text-white">An industry research newsletter</em>. Four announcements in one week: a card-issuing startup's APA, a cloud hyperscaler's agent payments launch, a major credit bureau Agent Trust, a global super-app wallet AHA.
                &ldquo;Which of your systems, payments or fraud, should handle it, is still unresolved.&rdquo;
              </p>
            </div>
            <div className="border-l-2 border-amber-500/40 pl-5 py-2">
              <div className="text-[10px] font-bold tracking-[1.5px] text-amber-400 mb-1">AUGUST 22 &middot; SENIOR PAYMENTS-INDUSTRY ANALYST (25+ YEARS)</div>
              <p className="text-slate-300 leading-relaxed">
                <em className="text-white">A payments trade publication</em>. On an agentic payments industry alliance's founding members.
                &ldquo;Not in the demo. In the dispute.&rdquo; And: &ldquo;Mandate. Evidence. Recourse. Reconciliation.&rdquo;
              </p>
            </div>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Three frameworks. Three vocabularies. One conclusion: the market has largely solved
            authorization and identity. Recourse: the layer that makes a harmed party whole
            after the transaction cleared and the fraud check passed and the agent still delivered the
            wrong thing: is where the next two years get decided.
          </p>
          <p className="text-slate-400 leading-relaxed italic">
            That is the layer this response is about, and the layer this month&rsquo;s news does not touch.
          </p>
          <div className="mt-6">
            <Link to="/three-analysts-one-gap" data-testid="link-three-analysts-essay">
              <Button size="sm" variant="outline" className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10">
                Read the full essay
                <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Close + PDF */}
      <section className="py-16 px-6 border-t border-white/5 bg-gradient-to-b from-transparent to-rose-950/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            We don&rsquo;t claim seven. We defend six, on-chain, today.
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            If the framework is wrong, tell us where. If a KYPA claim in this response overreaches, tell us
            where. If a supervisor, auditor, or lender needs a specific artifact we have not published yet,
            tell us which.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/downloads/kojiru-seven-conditions-response.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-white text-black hover:bg-slate-100"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download the response (PDF)
              </Button>
            </a>
            <a href="mailto:wayne@kojiru.com">
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="email-founder-btn"
              >
                wayne@kojiru.com
              </Button>
            </a>
          </div>
          <p className="text-xs text-slate-600 mt-8">
            Framework: <em>a digital-money podcast · The Agentic Economy · Episode 1</em> · 18 August 2026 · DH K.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
